/**
 * Client-side "recent searches" list, backed by localStorage (per browser, not per account —
 * there is no saved-searches API yet). This is what the landing page uses to tell a first-time
 * visitor from a returning one, and to fill the returning-user panel with real queries instead
 * of placeholder text.
 *
 * localStorage, not sessionStorage (contrast lib/lastSearch.js's "back to results" breadcrumb,
 * which is deliberately session-scoped): "returning user" needs to survive across sessions on
 * the same device, or every new tab would look like a first-ever visit.
 *
 * Storage can throw (Safari private mode, blocked/partitioned storage) -- every access is
 * guarded, same pattern as lib/lastSearch.js. Never worth failing a search over.
 */

const KEY = "explorer:recent-searches";
// Not literally unbounded (a pathological runaway caller shouldn't grow this file without
// limit), but high enough that no real user saving searches one at a time will ever hit it --
// well above SavedSearchesPanel.js's own pagination threshold, so that UI stays reachable.
const MAX_ENTRIES = 500;

function readAll() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Most recent first. `label` defaults to `query` for entries recorded before Rename existed --
 * applied at read time so those older entries don't need a migration.
 */
export function getRecentSearches() {
  return readAll().map((entry) => ({ ...entry, label: entry.label ?? entry.query }));
}

/**
 * `label` defaults to `query` when omitted, and preserves an existing custom label across a
 * re-save of the same query -- otherwise saving an already-renamed search again would silently
 * reset it back to the raw query text.
 *
 * `parameters` (optional): the structured `/api/search/execute` parse result. Persisting it lets
 * `LandingPage.js` run it through `parametersToSegments` to show real tokens when a saved search
 * is reselected, instead of just a plain, untokenized string.
 *
 * `originalSegments` (optional): the exact string|{pill} segments the user actually
 * typed/committed. Reconstructing from `parameters` alone replays the backend's own re-derived
 * interpretation, which can diverge from what was actually searched -- `LandingPage.js` prefers
 * this over `parameters` when both are present. Both are omitted (not even a key) for a caller
 * that doesn't pass one, so older entries and plain-text-only callers are unaffected.
 *
 * Returns whether the write actually landed. A storage failure (Safari private mode, blocked/
 * partitioned storage, quota exceeded) is still never worth THROWING over or blocking the rest of
 * a search over -- callers that don't care (e.g. the implicit per-visit recording) can keep
 * ignoring the return value entirely, same as before this was added. But a caller presenting this
 * as an explicit, user-initiated action (SearchResults.js's Save Search) needs to know it failed
 * instead of showing a "Search Saved" success toast for a write that silently did nothing.
 */
export function recordSearch(query, label, parameters, originalSegments) {
  if (!query || !query.trim()) return false;
  try {
    const all = readAll();
    const existingLabel = all.find((entry) => entry.query === query)?.label;
    const deduped = all.filter((entry) => entry.query !== query);
    const updated = [
      {
        query,
        label: label?.trim() || existingLabel || query,
        searchedAt: Date.now(),
        ...(parameters ? { parameters } : {}),
        ...(originalSegments ? { originalSegments } : {}),
      },
      ...deduped,
    ].slice(0, MAX_ENTRIES);
    window.localStorage.setItem(KEY, JSON.stringify(updated));
    return true;
  } catch {
    /* not worth failing the search over */
    return false;
  }
}

/** Rename: a separate, persisted label distinct from the underlying query --
 * renaming must not mean overwriting the query itself, which would break re-running it. */
export function renameSearch(query, newLabel) {
  if (!query || !newLabel || !newLabel.trim()) return;
  try {
    const updated = readAll().map((entry) =>
      entry.query === query ? { ...entry, label: newLabel.trim() } : entry
    );
    window.localStorage.setItem(KEY, JSON.stringify(updated));
  } catch {
    /* not worth failing over */
  }
}

/** Delete. */
export function deleteSearch(query) {
  try {
    const updated = readAll().filter((entry) => entry.query !== query);
    window.localStorage.setItem(KEY, JSON.stringify(updated));
  } catch {
    /* not worth failing over */
  }
}

/**
 * How many searches this browser has ever executed -- separate from the capped/deduped
 * list above, which the landing-page headline/template-copy thresholds can't use directly:
 * re-running the same query 5 times leaves that list at length 1, but the user has executed 5
 * searches. This counter is for those two thresholds only -- keep it separate from whatever
 * Saved Searches turns out to be; "how many times you've searched" and "what you've explicitly
 * saved" are two different things.
 */
const COUNT_KEY = "explorer:executed-search-count";

export function getSearchCount() {
  try {
    const raw = window.localStorage.getItem(COUNT_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

export function incrementSearchCount() {
  try {
    window.localStorage.setItem(COUNT_KEY, String(getSearchCount() + 1));
  } catch {
    /* not worth failing the search over */
  }
}

/**
 * The search bar's own "RECENT SELECTIONS" quick-pick list -- individually-committed
 * *tokens*, not whole queries like the list above. Separate storage/shape/purpose: this backs the
 * command palette's empty-focus state for a returning user, the whole-query list above backs the
 * landing page's Saved Searches rail. Same localStorage-backed, guarded-access pattern.
 */
const SELECTIONS_KEY = "explorer:recent-selections";
const MAX_SELECTIONS = 5;

function readAllSelections() {
  try {
    const raw = window.localStorage.getItem(SELECTIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Most recent first, up to 5. */
export function getRecentSelections() {
  return readAllSelections();
}

export function recordSelection({ display, category } = {}) {
  if (!display) return;
  try {
    const deduped = readAllSelections().filter((entry) => entry.display !== display);
    const updated = [{ display, category, selectedAt: Date.now() }, ...deduped].slice(
      0,
      MAX_SELECTIONS
    );
    window.localStorage.setItem(SELECTIONS_KEY, JSON.stringify(updated));
  } catch {
    /* not worth failing the search over */
  }
}

/**
 * Re-resolves each stored {display, category} against the live term index by exact display
 * match, so picking one is a genuinely editable token, not stale metadata. A selection whose
 * term no longer exists resolves to a plain unresolved token instead. Shared by
 * TokenizedSearchInput.js's two "recent selections" uses (the empty-bar state and the ADD TO
 * YOUR SEARCH fallback) instead of duplicating this resolution rule.
 */
export function resolveRecentSelections(termIndex) {
  return getRecentSelections().map((entry) => {
    const resolved = termIndex.find(
      (term) => term.display.toLowerCase() === entry.display.toLowerCase()
    );
    return (
      resolved ?? {
        id: `recent:${entry.display}`,
        display: entry.display,
        category: entry.category,
        sourceType: "unresolved",
        siblings: [],
      }
    );
  });
}
